/**
 * Prepare Initializer Component (V3 API only)
 *
 * For v3 API flow (JWT tokens), calls the prepare endpoint to get transaction context
 * (operation, lang, assetCode, transactionId, walletProvider) which replaces URL params.
 *
 * For v2/v5 API flow, this component does nothing as context comes from URL params.
 */

import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { captureException } from "@/utils/error-tracking";
import { useApiClientWithAutoVersion } from "../hooks/useApiClientWithAutoVersion";
import { switchLanguage } from "../lib/i18n-loader";
import { parseSupportedLanguageFromString } from "../lib/language-detection";
import { normalizeOperation } from "../lib/normalize-operation";
import { rampsStore } from "../stores/ramps-store";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";
import { normalizeWalletProvider } from "../utils/analytics";

export function PrepareInitializer() {
	const store = useRampsStore();
	const hasCalledPrepare = useRef(false);
	const navigate = useNavigate();
	const search = useSearch({ strict: false });

	// Use the same API client pattern as the rest of the app (handles proxy correctly)
	const { apiClient, apiVersion } = useApiClientWithAutoVersion({
		authToken: store.token,
	});

	// Determine if we should call prepare (v3 only)
	const token = store.token;

	// Note: Token doesn't change during a transaction, so we don't need to reset
	// hasCalledPrepare. It will persist for the entire transaction lifecycle.

	const isTransactionStatusPage = window.location.pathname.includes(
		"/transaction-status",
	);
	const shouldCallPrepare = Boolean(
		apiVersion === "v3" &&
			token &&
			!hasCalledPrepare.current &&
			!isTransactionStatusPage,
	);

	// Log when prepare will be called (v3 flow)

	// Query for prepare endpoint (v3 only)
	const {
		data: prepareData,
		isSuccess,
		isError,
	} = useQuery({
		queryKey: ["prepare", token, apiVersion],
		queryFn: async () => {
			return apiClient.transaction.prepare();
		},
		enabled: shouldCallPrepare,
		staleTime: Number.POSITIVE_INFINITY, // Never refetch - prepare is called once per session
		retry: 2,
	});

	// RAMPS-4405: a failed prepare (after retries) leaves the flow unrecoverable —
	// flag it so the landing pages can show the core-API error splash.
	useEffect(() => {
		if (isError) {
			rampsActions.setPrepareFailed();
		}
	}, [isError]);

	// Store prepare response data and align Lingui with prepare `lang` (URL often omits it for v3)
	useEffect(() => {
		if (!isSuccess || !prepareData || hasCalledPrepare.current) {
			return;
		}

		hasCalledPrepare.current = true;

		const operation = normalizeOperation(prepareData.operation);

		const { wallet_provider: normalizedProvider } = normalizeWalletProvider(
			prepareData.walletProvider,
		);

		rampsActions.setPrepareData({
			operation,
			lang: prepareData.lang,
			assetCode: prepareData.assetCode,
			transactionId: prepareData.transactionId,
			walletProvider: normalizedProvider,
			amount: prepareData.amount,
		});

		// No corrective session_started here: wallet_provider is resolved from the
		// JWT `client_name` claim before the first event fires (resolvePartner in
		// utils/analytics), so the single session_started from SessionInitializer
		// is already attributed. Re-emitting it double-counted every v3 session.

		const linguiLocale = parseSupportedLanguageFromString(
			prepareData.lang ?? "en",
		);

		let cancelled = false;

		// RAMPS-4268: do NOT overwrite a URL-driven locale with prepareData.lang.
		// `setLocaleFromUrl` in main.tsx marks the source as `url-*` when the
		// partner passed `app.languages` or `lang` in the iframe URL — in that
		// case the bootstrap-time activation is authoritative and prepareData.lang
		// (which is whatever was persisted on the transaction at creation time)
		// would just clobber the partner-supplied preference.
		const urlDrivenLangSource =
			rampsStore.state.langSource === "url-app-languages" ||
			rampsStore.state.langSource === "url-lang";

		void (async () => {
			if (urlDrivenLangSource) {
				console.log(
					"skipping language switch after prepare — URL-driven locale takes precedence",
					{ urlLang: rampsStore.state.lang, prepareLang: prepareData.lang },
				);
			} else {
				try {
					console.log("switching language to", linguiLocale);
					await switchLanguage(linguiLocale);
				} catch (error) {
					captureException(error, {
						tags: {
							component: "PrepareInitializer",
							action: "switchLanguageAfterPrepare",
						},
						extra: { lang: prepareData.lang },
					});
					console.error("Failed to switch language after prepare:", error);
				}
			}

			if (cancelled) {
				return;
			}

			if (
				operation === "deposit" &&
				!window.location.pathname.includes("/transaction-status")
			) {
				// When the URL drove the locale, the partner's preference is
				// authoritative. Preserve whatever lang is already on the URL
				// (which validateRampsSearch keeps in sync with store.lang)
				// rather than writing the prepareData-derived Lingui code.
				const navigationLang = urlDrivenLangSource
					? search.lang || rampsStore.state.lang
					: linguiLocale;
				navigate({
					to: "/ramps/deposit",
					search: {
						...search,
						transaction_id: prepareData.transactionId || "",
						wallet_provider: normalizedProvider,
						asset_code: prepareData.assetCode || "USDC",
						operation,
						lang: navigationLang,
						amount: "",
						country: "",
						state: "",
						step: "deposit",
					},
				});
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [isSuccess, prepareData, navigate, search]);

	// This is an initialization component - renders nothing
	return null;
}
